import React from 'react';
import { httpsCallable } from 'firebase/functions';
import * as LucideIcons from 'lucide-react';

import { useAppContext } from '../AppContext';
import HoverTooltip from './HoverTooltip';
import BadgeIconStrip from './BadgeIconStrip';
import { useTranslation } from 'react-i18next';

function NexusMembers({ members, nexus, onUserSelect, userProfiles }) {
    const { t } = useTranslation();
    const { userId, appFunctions, setMessage, showConfirmation } = useAppContext();
    const currentUserMember = members.find(m => m.id === userId);
    const currentUserRole = currentUserMember?.role;
    const isOwner = nexus.ownerId === userId;

    const handleManageMember = (targetUserId, action) => {
        const targetProfile = userProfiles.find(p => p.id === targetUserId);
        const message = t('nexusMembers.manageConfirmation', { action, displayName: targetProfile.displayName });
        showConfirmation({
            message,
            onConfirm: async () => {
                const manageNexusMembers = httpsCallable(appFunctions, 'manageNexusMembers');
                try {
                    await manageNexusMembers({ nexusId: nexus.id, targetUserId, action });
                    setMessage(t('nexusMembers.manageSuccess', { action }));
                } catch (error) {
                    setMessage(`${t('nexusMembers.manageFailed')} ${error.message}`);
                }
            }
        });
    };

    const handleDeletePersona = (aiProfile) => {
        showConfirmation({
            message: t('nexusMembers.deletePersonaConfirmation', { displayName: aiProfile.displayName }), 
            onConfirm: async () => {
                const deleteAiPersona = httpsCallable(appFunctions, 'deleteAiPersona');
                try {
                    await deleteAiPersona({ aiUserId: aiProfile.id });
                    setMessage(t('nexusMembers.deletePersonaSuccess'));
                } catch (error) {
                    setMessage(`${t('nexusMembers.deletePersonaFailed')} ${error.message}`);
                }
            }
        });
    };

    return (
        <div className="space-y-2">
            {members.map(member => {
                const profile = userProfiles.find(p => p.id === member.id);
                if (!profile) return null;

                const canManage = (currentUserRole === 'owner' && member.role !== 'owner') || (currentUserRole === 'moderator' && member.role === 'member');
                const isAI = profile.isAI;

                return (
                    <div key={member.id} className="bg-gray-800/50 p-3 rounded-lg flex items-center justify-between">
                        <div onClick={() => !isAI && onUserSelect(member.id)} className={`flex items-center gap-3 ${!isAI && 'cursor-pointer'}`}>
                            <img src={profile.photoURL || "https://placehold.co/40x40/AEC6CF/FFFFFF?text=U"} alt={profile.displayName} className="w-10 h-10 rounded-full object-cover" />
                            <div className="flex items-center gap-2">
                                <p className="font-semibold text-white">{profile.displayName}</p>
                                <BadgeIconStrip badges={profile.badges} />
                                {isAI && <LucideIcons.BrainCircuit size={14} className="text-purple-400" />}
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            {member.role === 'owner' && (
                                <span className="text-xs font-bold text-amber-400 flex items-center gap-1 bg-amber-900/30 px-2 py-1 rounded-full border border-amber-500/50 shadow-[0_0_10px_rgba(251,191,36,0.3)]">
                                    <LucideIcons.Crown size={14} /> Nexus Lord
                                </span>
                            )}
                            {member.role === 'moderator' && (
                                <span className="text-xs font-bold text-sky-400 flex items-center gap-1 bg-sky-900/30 px-2 py-1 rounded-full border border-sky-500/50 shadow-[0_0_10px_rgba(56,189,248,0.3)]">
                                    <LucideIcons.Shield size={14} /> Guardian
                                </span>
                            )}
                            {member.role === 'member' && (
                                <span className="text-xs font-bold text-purple-300 flex items-center gap-1 bg-purple-900/30 px-2 py-1 rounded-full border border-purple-500/30">
                                    <LucideIcons.Sparkles size={12} /> Initiate
                                </span>
                            )}
                            {canManage && !isAI && (
                                <div className="flex gap-1">
                                    {currentUserRole === 'owner' && member.role === 'member' && <HoverTooltip text={t('nexusMembers.tooltips.promote')}><button onClick={() => handleManageMember(member.id, 'promote')} className="ai-icon-button text-sky-400 hover:bg-sky-900/50"><LucideIcons.ChevronUpSquare size={16} /></button></HoverTooltip>}
                                    {currentUserRole === 'owner' && member.role === 'moderator' && <HoverTooltip text={t('nexusMembers.tooltips.demote')}><button onClick={() => handleManageMember(member.id, 'demote')} className="ai-icon-button text-gray-400 hover:bg-gray-900/50"><LucideIcons.ChevronDownSquare size={16} /></button></HoverTooltip>}
                                    <HoverTooltip text={t('nexusMembers.tooltips.kick')}><button onClick={() => handleManageMember(member.id, 'kick')} className="ai-icon-button text-red-500 hover:bg-red-900/50"><LucideIcons.XSquare size={16} /></button></HoverTooltip>
                                </div>
                            )}
                            {isOwner && isAI && (
                                <HoverTooltip text={t('nexusMembers.tooltips.deletePersona')}>
                                    <button onClick={() => handleDeletePersona(profile)} className="ai-icon-button text-red-500 hover:bg-red-900/50">
                                        <LucideIcons.Trash2 size={16} />
                                    </button>
                                </HoverTooltip>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default NexusMembers;